<script setup lang="ts">
import { Order } from '@commercetools/platform-sdk';
import { Form } from 'vee-validate';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { FromNutsJsonOptions, KeyedError } from '@/api';
import { InputFailedEvent } from '@/components/base/form/InlineError.vue';
import PasswordInput from '@/components/base/form/PasswordInput.vue';
import Radio from '@/components/base/form/Radio.vue';
import Notification from '@/components/base/Notification.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import Card from '@/components/checkout/orderReceipt/Card.vue';
import { useRouteChange } from '@/composables/navigation/useRouteChange';
import { useCheckout } from '@/composables/useCheckout';
import { useForm } from '@/composables/useForm';
import { useCustomer } from '@/stores/customer';
import { NutsAddress } from '@/utils/address';
import { CheckoutEvents } from '@/utils/analytics';
import { reportError } from '@/utils/reportError';

const props = defineProps<{
  order: Order;
  accountStatusNotification?: { key: string; message: string };
}>();

const store = useStore();
const showAccountType = ref(true);
const { tryCreateCustomerOrSignIn, isB2B } = useCheckout(store);
const { validatorFailed } = useForm();
const { navigateTo } = useRouteChange(useRouter());

const createAccountPassword = ref('');
const isB2b = ref(isB2B.value);
const loading = ref(false);
const accountStatus = ref(props.accountStatusNotification?.key);
const accountMessage = ref(props.accountStatusNotification?.message);
const showB2BRegistration = ref(accountStatus.value === 'customerCreated' && isB2b.value);

const createAccount = async () => {
  const { customerEmail, billingAddress } = props.order;
  if (billingAddress) {
    const onMessages: Exclude<FromNutsJsonOptions['onMessages'], 'default'> = (messages) => {
      const messagesCombined = [
        ...(messages.errors ?? []),
        ...(messages.warnings ?? []),
        ...(messages.notices ?? []),
      ];
      const notification = messagesCombined.filter((message) =>
        [
          'customerCreated',
          'customerExisted',
          'customerCreationFailed',
          'customerExistedWithDifferentPassword',
        ].includes(message.key),
      )[0];
      if (notification) {
        accountStatus.value = notification.key;
        accountMessage.value = notification.message;
      }
    };
    const { name } = NutsAddress.fromCt(billingAddress);
    try {
      const email = customerEmail ?? '';
      loading.value = true;
      const newCustomer = {
        email,
        name: name ?? '',
        password: createAccountPassword.value,
        onMessages,
        isB2b: isB2b.value,
      };
      await tryCreateCustomerOrSignIn(newCustomer, 'Receipt');
      showB2BRegistration.value = !!useCustomer().customer && !!useCustomer().contact?.isB2b;
      loading.value = false;
    } catch (error) {
      accountMessage.value = reportError(error);
      if (error instanceof KeyedError) {
        accountStatus.value = error.key;
      }
      loading.value = false;
    }
    loading.value = false;
  }
};

const viewAccount = () => {
  navigateTo('/account');
};

const completeB2BRegistration = () => {
  sessionStorage.setItem('B2BAccountSource', 'Checkout Registration');
  navigateTo('/business/register');
};

onMounted(async () => {
  if (isB2B.value) showAccountType.value = false;
});

const onPasswordInputFailed = ({ field, error }: InputFailedEvent) => {
  CheckoutEvents.validation({ stepNumber: 5, field, error });
};
</script>

<template>
  <Card :class="{ 'opacity-50 pointer-events-none': loading }">
    <template v-if="showB2BRegistration">
      <LargeBodyText class="font-sofia-pro">Welcome to the family!</LargeBodyText>
      <BaseBodyText class="mt-3">
        Your business account has been successfully created! Complete the verification step to get
        full access to the exclusive business features
      </BaseBodyText>
      <ThemedButton class="my-6" fullWidth theme="gray" @click="completeB2BRegistration">
        Complete Registration
      </ThemedButton>
      <SmallBodyText>
        Save time and get exclusive deals with a verified business account
      </SmallBodyText>
    </template>
    <template v-else-if="accountStatus === 'customerCreated'">
      <LargeBodyText class="font-sofia-pro" data-test="receipt-welcome-title"
        >Welcome to the family!</LargeBodyText
      >
      <BaseBodyText class="mt-3">
        We've created an account for you with the password you provided. You can now access your
        account below.
      </BaseBodyText>
      <ThemedButton
        class="mt-6"
        data-test="receipt-view-account-button"
        fullWidth
        theme="gray"
        @click="viewAccount"
      >
        View Account
      </ThemedButton>
    </template>
    <template v-else-if="accountStatus === 'customerExisted'">
      <LargeBodyText class="font-sofia-pro">You're already in!</LargeBodyText>
      <BaseBodyText class="mt-3">
        It seems you already have an account, so we've logged you in instead. You can now access
        your account below.
      </BaseBodyText>
      <ThemedButton class="mt-6" fullWidth theme="gray" @click="viewAccount">
        View Account
      </ThemedButton>
    </template>
    <template v-else-if="accountStatus === 'customerExistedWithDifferentPassword'">
      <Notification theme="error" class="mb-4">
        {{ accountMessage }}
      </Notification>
      <Form v-slot="{ meta }" @submit="createAccount">
        <PasswordInput v-model="createAccountPassword" class="mt-5" requiredOnly />
        <ThemedButton
          :disabled="validatorFailed(meta)"
          :isLoading="loading"
          class="mt-6"
          fullWidth
          theme="gray"
          type="submit"
        >
          Sign into Account
        </ThemedButton>
      </Form>
    </template>
    <template v-else>
      <div class="inline-flex items-center">
        <LargeBodyText class="mr-2">Create an Account</LargeBodyText>
        <span class="text-sm leading-5 text-neutral-500">(optional)</span>
      </div>
      <Notification
        v-if="accountStatus === 'customerCreationFailed'"
        class="mt-3 md:mt-6"
        theme="error"
      >
        {{ accountMessage }}
      </Notification>
      <Form v-slot="{ meta }" @submit="createAccount">
        <div class="mt-2 text-sm text-black md:pr-2">
          Checkout faster, track your packages, create favorites &amp; more!
        </div>
        <div
          v-if="showAccountType"
          class="flex mt-3"
          aria-label="Account Type"
          data-test="account-type-selector"
          role="radiogroup"
        >
          <div class="mr-5">
            <Radio
              v-model="isB2b"
              aria-label="Personal"
              class="text-base"
              name="account-type"
              :value="false"
            >
              Personal
            </Radio>
          </div>
          <div class="mr-5">
            <Radio
              v-model="isB2b"
              aria-label="Business"
              class="text-base"
              name="account-type"
              :value="true"
            >
              Business
            </Radio>
          </div>
        </div>
        <div class="mt-2">
          <PasswordInput v-model="createAccountPassword" @input-failed="onPasswordInputFailed" />
        </div>
        <ThemedButton
          :disabled="validatorFailed(meta)"
          :isLoading="loading"
          class="mt-6"
          fullWidth
          theme="gray"
          type="submit"
        >
          Create an Account
        </ThemedButton>
      </Form>
    </template>
  </Card>
</template>
